import React, { useState } from "react";
import { toast } from "sonner";
import { PrimaryBtn, GhostBtn, Modal } from "./CrmUI";

// Minimal RFC-4180-ish CSV parser: handles quoted cells, escaped quotes and
// CRLF. Header row is lower-cased and spaces -> underscores so "ISB Batch"
// maps to isb_batch.
export const parseCsv = (text) => {
    const lines = String(text).split(/\r?\n/).filter((l) => l.trim());
    if (!lines.length) return [];
    const split = (line) => {
        const out = []; let cur = ""; let q = false;
        for (let i = 0; i < line.length; i++) {
            const ch = line[i];
            if (ch === '"') { if (q && line[i + 1] === '"') { cur += '"'; i++; } else q = !q; }
            else if (ch === "," && !q) { out.push(cur); cur = ""; }
            else cur += ch;
        }
        out.push(cur); return out;
    };
    const headers = split(lines[0]).map((h) => h.trim().toLowerCase().replace(/\s+/g, "_"));
    return lines.slice(1).map((line) => {
        const cells = split(line);
        const row = {};
        headers.forEach((h, i) => { row[h] = (cells[i] || "").trim(); });
        return row;
    });
};

// Reusable CSV import modal. `onImport(rows)` should resolve to
// `{ imported, updated, skipped }` (the import endpoint's response).
export const CsvImportModal = ({ open, onClose, onImport, onDone, title, subtitle, hint, testId = "csv-modal" }) => {
    const [rows, setRows] = useState([]);
    const [busy, setBusy] = useState(false);

    const onFile = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const reader = new FileReader();
        reader.onload = () => { setRows(parseCsv(String(reader.result))); };
        reader.readAsText(file);
    };

    const doImport = async () => {
        if (!rows.length) { toast.error("Nothing to import."); return; }
        setBusy(true);
        try {
            const r = await onImport(rows);
            const parts = [`${r.imported} added`];
            if (r.updated) parts.push(`${r.updated} updated`);
            if (r.skipped) parts.push(`${r.skipped} unchanged`);
            toast.success(`Import complete — ${parts.join(", ")}.`);
            onDone(); onClose(); setRows([]);
        }
        catch (e) { toast.error(e?.response?.data?.detail || "Import failed."); }
        finally { setBusy(false); }
    };

    return (
        <Modal open={open} onClose={onClose} title={title} subtitle={subtitle} maxWidth="max-w-xl" testId={testId}>
            <div className="rounded-2xl border border-dashed border-oth-line bg-white p-6 text-center">
                <input type="file" accept=".csv" onChange={onFile} className="block w-full text-sm font-body text-oth-ink2 file:mr-3 file:px-4 file:py-2 file:rounded-full file:border-0 file:bg-[#0A0A0A] file:text-white file:text-sm file:cursor-pointer" data-testid="csv-file" />
                <div className="text-[11px] text-oth-ink3 mt-3 font-body">{hint}</div>
            </div>
            {rows.length > 0 && (
                <div className="mt-4 text-sm font-body text-oth-ink2" data-testid="csv-preview">
                    <span className="font-medium text-oth-ink">{rows.length}</span> rows detected. First: <span className="text-oth-ink">{rows[0].name || "(no name)"}</span>
                </div>
            )}
            <div className="flex items-center justify-end gap-3 mt-6">
                <GhostBtn onClick={onClose} testId="csv-cancel">Cancel</GhostBtn>
                <PrimaryBtn onClick={doImport} disabled={busy || !rows.length} testId="csv-import">{busy ? "Importing…" : "Import"}</PrimaryBtn>
            </div>
        </Modal>
    );
};
